/**
 * Pure functions for particle physics and alpha calculation.
 *
 * Life values are in milliseconds. Velocity is in px/s.
 * Alpha fades on a path-based curve: full opacity → fade → dead.
 * Adjust PARTICLE_FADE_START_MS / PARTICLE_FADE_DURATION_MS in constants
 * and the curve updates automatically.
 */

import { PARTICLE_FADE_START_MS, PARTICLE_FADE_DURATION_MS } from '../utils/constants';
import type { Particle } from '../utils/types';

/**
 * Computes draw alpha for a particle based on elapsed life.
 * Full opacity for the first PARTICLE_FADE_START_MS,
 * then linear fade to 0 over PARTICLE_FADE_DURATION_MS.
 */
export function getParticleAlpha(particle: Particle): number {
  const elapsed = particle.maxLife - particle.life;
  if (elapsed < PARTICLE_FADE_START_MS) return 1.0;
  const fadeProgress = (elapsed - PARTICLE_FADE_START_MS) / PARTICLE_FADE_DURATION_MS;
  return Math.max(0, 1 - fadeProgress);
}

/**
 * Updates a particle's position and life in-place.
 * Returns true if the particle is still alive.
 *
 * @param particle - mutated directly (no allocation)
 * @param deltaMs  - elapsed time in milliseconds since last frame
 */
export function updateParticle(particle: Particle, deltaMs: number): boolean {
  particle.x += particle.vx * (deltaMs / 1000);
  particle.y += particle.vy * (deltaMs / 1000);
  particle.life -= deltaMs;
  return particle.life > 0;
}
